import { CommonFilterModel, CommonParams } from '@/api/resources/common/shared/models/common.models'
import { AgGridColumnType, AgGridExportType, ExportedColDef, ExportedColGroupDef } from '@/shared/models/ag-grid.model'
import { ICellRendererParams } from '@ag-grid-community/core/dist/cjs/rendering/cellRenderers/iCellRenderer'
import buildIconHeaderTemplate from '@/grid/buildIconHeaderTemplate'
import i18n from '@/lang/i18n'
import { ScientificAnalysisDatasource } from '@/modules/scientific-analysis-module/components/scientific-analysis-results-panel/datasources/scientific-analysis-datasource'
import { ScientificAnalysisEntityCase } from '@/modules/scientific-analysis-module/services/scientific-analysis-dto-case'
import { scientificAnalysisService } from '@/modules/scientific-analysis-module/services/scientific-analysis-services'
import { Nullable } from '@/shared/types/app.types'
import { ColDef } from '@ag-grid-community/core'
import CellGridRichText from '@/components/common/CellGridRichText.vue'
import { InfinitePaginatorResponse } from '@/shared/resources/infinite-datasources/infinite-resource.model'
import { ScientificAnalysisPatientDatasource } from '@/modules/scientific-analysis-module/components/scientific-analysis-results-panel/datasources/scientific-analysis-datasource-patient'

export class ScientificAnalysisCaseDatasource extends ScientificAnalysisDatasource<ScientificAnalysisEntityCase> {
    public static getEntityGridColumns(selector: string = '', hideAll: boolean = false): (ColDef | ExportedColDef | ExportedColGroupDef) {
        return {
            headerName: i18n.tc(`entities.case.case`),
            children: [
                {
                    type: AgGridColumnType.TEXT,
                    headerName: i18n.tc(`scientificAnalysis.grid.results.case.header`),
                    exportField: `${selector}caseNumber`,
                    exportType: AgGridExportType.TEXT,
                    field: `${selector}caseNumber`,
                    sortable: false,
                    filter: false,
                    minWidth: 200,
                    flex: 1,
                    hide: hideAll
                },
                {
                    type: AgGridColumnType.TEXT,
                    headerName: i18n.tc(`scientificAnalysis.grid.results.case.requestNumber`),
                    exportField: `${selector}requestNumber`,
                    exportType: AgGridExportType.TEXT,
                    field: `${selector}requestNumber`,
                    sortable: false,
                    filter: false,
                    minWidth: 200,
                    flex: 1,
                    hide: hideAll
                },
                {
                    type: AgGridColumnType.TEXT,
                    headerName: i18n.tc(`scientificAnalysis.grid.results.case.caseType`),
                    exportField: `${selector}caseType.description`,
                    exportType: AgGridExportType.TEXT,
                    field: `${selector}caseType.description`,
                    sortable: false,
                    filter: false,
                    minWidth: 200,
                    flex: 1,
                    hide: hideAll
                },
                {
                    type: AgGridColumnType.COMMON_CASE_STATUS,
                    headerName: i18n.tc(`scientificAnalysis.grid.results.case.status`),
                    exportField: `${selector}status.name`,
                    exportType: AgGridExportType.TEXT,
                    field: `${selector}status`,
                    sortable: false,
                    filter: false,
                    flex: 1,
                    hide: hideAll
                },
                {
                    type: AgGridColumnType.DATETIME,
                    headerName: i18n.tc(`scientificAnalysis.grid.results.case.registrationDate`),
                    exportField: `${selector}registrationDate`,
                    exportType: AgGridExportType.DATETIME,
                    field: `${selector}registrationDate`,
                    sortable: false,
                    filter: false,
                    minWidth: 200,
                    flex: 1,
                    hide: hideAll
                },
                {
                    type: AgGridColumnType.DATETIME,
                    headerName: i18n.tc(`scientificAnalysis.grid.results.case.validationDate`),
                    exportField: `${selector}validationDate`,
                    exportType: AgGridExportType.DATETIME,
                    field: `${selector}validationDate`,
                    sortable: false,
                    filter: false,
                    minWidth: 200,
                    flex: 1,
                    hide: hideAll
                },
                {
                    type: AgGridColumnType.TEXT,
                    headerName: i18n.tc(`scientificAnalysis.grid.results.case.validationResponsible`),
                    exportField: `${selector}validationResponsible.fullName`,
                    exportType: AgGridExportType.TEXT,
                    field: `${selector}validationResponsible.fullName`,
                    sortable: false,
                    filter: false,
                    minWidth: 200,
                    flex: 1,
                    hide: hideAll
                },
                {
                    type: AgGridColumnType.TEXT,
                    headerName: i18n.tc(`scientificAnalysis.grid.results.case.pathologist`),
                    exportField: `${selector}pathologist.fullName`,
                    exportType: AgGridExportType.TEXT,
                    field: `${selector}pathologist.fullName`,
                    sortable: false,
                    filter: false,
                    minWidth: 200,
                    flex: 1,
                    hide: hideAll
                },
                {
                    type: AgGridColumnType.PRIORITY,
                    headerName: i18n.tc(`scientificAnalysis.grid.results.case.priority`),
                    exportField: `${selector}priority.name`,
                    exportType: AgGridExportType.TEXT,
                    field: `${selector}priority.level`,
                    sortable: false,
                    filter: false,
                    flex: 1,
                    hide: hideAll
                },
                {
                    type: AgGridColumnType.DATETIME,
                    headerName: i18n.tc(`scientificAnalysis.grid.results.case.requestDate`),
                    exportField: `${selector}requestDate`,
                    exportType: AgGridExportType.DATETIME,
                    field: `${selector}requestDate`,
                    sortable: false,
                    filter: false,
                    minWidth: 200,
                    flex: 1,
                    hide: true
                },
                {
                    type: AgGridColumnType.DATETIME,
                    headerName: i18n.tc(`scientificAnalysis.grid.results.case.informedDate`),
                    exportField: `${selector}informedDate`,
                    exportType: AgGridExportType.DATETIME,
                    field: `${selector}informedDate`,
                    sortable: false,
                    filter: false,
                    flex: 1,
                    minWidth: 200,
                    hide: true
                },
                {
                    type: AgGridColumnType.DATETIME,
                    headerName: i18n.tc(`scientificAnalysis.grid.results.case.transcriptionDate`),
                    exportField: `${selector}transcriptionDate`,
                    exportType: AgGridExportType.DATETIME,
                    field: `${selector}transcriptionDate`,
                    sortable: false,
                    filter: false,
                    flex: 1,
                    minWidth: 200,
                    hide: true
                },
                {
                    type: AgGridColumnType.DATETIME,
                    headerName: i18n.tc(`scientificAnalysis.grid.results.case.amendmentDate`),
                    exportField: `${selector}amendmentDate`,
                    exportType: AgGridExportType.DATETIME,
                    field: `${selector}amendmentDate`,
                    sortable: false,
                    filter: false,
                    flex: 1,
                    minWidth: 200,
                    hide: true
                },
                {
                    type: AgGridColumnType.TEXT,
                    headerName: i18n.tc(`scientificAnalysis.grid.results.case.physicianHealthManagementArea`),
                    exportField: `${selector}physicianHealthManagementArea.name`,
                    exportType: AgGridExportType.TEXT,
                    field: `${selector}physicianHealthManagementArea.name`,
                    sortable: false,
                    filter: false,
                    flex: 1,
                    minWidth: 200,
                    hide: true
                },
                {
                    type: AgGridColumnType.TEXT,
                    headerName: i18n.tc(`scientificAnalysis.grid.results.case.physicianCenter`),
                    exportField: `${selector}physicianDepartment.center.name`,
                    exportType: AgGridExportType.TEXT,
                    field: `${selector}physicianDepartment.center.name`,
                    sortable: false,
                    filter: false,
                    flex: 1,
                    minWidth: 200,
                    hide: true
                },
                {
                    type: AgGridColumnType.TEXT,
                    headerName: i18n.tc(`scientificAnalysis.grid.results.case.physicianUnit`),
                    exportField: `${selector}physicianDepartment.name`,
                    exportType: AgGridExportType.TEXT,
                    field: `${selector}physicianDepartment.name`,
                    sortable: false,
                    filter: false,
                    flex: 1,
                    minWidth: 200,
                    hide: true
                },
                {
                    type: AgGridColumnType.TEXT,
                    headerName: i18n.tc(`scientificAnalysis.grid.results.case.destinationHealthManagementArea`),
                    exportField: `${selector}destinationHealthManagementArea.name`,
                    exportType: AgGridExportType.TEXT,
                    field: `${selector}destinationHealthManagementArea.name`,
                    sortable: false,
                    filter: false,
                    flex: 1,
                    minWidth: 200,
                    hide: true
                },
                {
                    type: AgGridColumnType.TEXT,
                    headerName: i18n.tc(`scientificAnalysis.grid.results.case.destinationCenter`),
                    exportField: `${selector}destinationDepartment.center.name`,
                    exportType: AgGridExportType.TEXT,
                    field: `${selector}destinationDepartment.center.name`,
                    sortable: false,
                    filter: false,
                    flex: 1,
                    minWidth: 200,
                    hide: true
                },
                {
                    type: AgGridColumnType.TEXT,
                    headerName: i18n.tc(`scientificAnalysis.grid.results.case.destinationUnit`),
                    exportField: `${selector}destinationDepartment.name`,
                    exportType: AgGridExportType.TEXT,
                    field: `${selector}destinationDepartment.name`,
                    sortable: false,
                    filter: false,
                    flex: 1,
                    minWidth: 200,
                    hide: true
                },
                {
                    type: AgGridColumnType.TEXT,
                    headerName: i18n.tc(`scientificAnalysis.grid.results.case.receiver`),
                    exportField: `${selector}destination`,
                    exportType: AgGridExportType.TEXT,
                    field: `${selector}destination`,
                    sortable: false,
                    filter: false,
                    flex: 1,
                    minWidth: 200,
                    hide: true
                },
                {
                    type: AgGridColumnType.TEXT,
                    headerName: i18n.tc(`scientificAnalysis.grid.results.case.regime`),
                    exportField: `${selector}regime.name`,
                    exportType: AgGridExportType.TEXT,
                    field: `${selector}regime.name`,
                    sortable: false,
                    filter: false,
                    flex: 1,
                    minWidth: 200,
                    hide: true
                },
                {
                    type: AgGridColumnType.TEXT,
                    headerName: i18n.tc(`scientificAnalysis.grid.results.case.episode`),
                    exportField: `${selector}episode`,
                    exportType: AgGridExportType.TEXT,
                    field: `${selector}episode`,
                    sortable: false,
                    filter: false,
                    flex: 1,
                    minWidth: 200,
                    hide: true
                },
                {
                    type: AgGridColumnType.TEXT,
                    headerName: i18n.tc(`scientificAnalysis.grid.results.case.relevantClinicData`),
                    exportField: `${selector}clinicalData`,
                    exportType: AgGridExportType.TEXT,
                    field: `${selector}clinicalData`,
                    sortable: false,
                    filter: false,
                    flex: 1,
                    minWidth: 200,
                    hide: true
                },
                {
                    type: AgGridColumnType.PATIENT_AGE,
                    headerName: i18n.tc(`scientificAnalysis.grid.results.case.agePatient`),
                    exportField: `${selector}agePatient`,
                    exportType: AgGridExportType.NUMBER,
                    field: `${selector}agePatient`,
                    sortable: false,
                    filter: false,
                    flex: 1,
                    minWidth: 200,
                    hide: true
                },
                {
                    type: AgGridColumnType.TEXT,
                    headerName: i18n.tc(`scientificAnalysis.grid.results.case.observations`),
                    exportField: `${selector}observations`,
                    exportType: AgGridExportType.TEXT,
                    field: `${selector}observations`,
                    sortable: false,
                    filter: false,
                    flex: 1,
                    minWidth: 200,
                    hide: true
                },
                {
                    type: AgGridColumnType.TEXT,
                    headerName: i18n.tc(`scientificAnalysis.grid.results.case.registerer`),
                    exportField: `${selector}registerer.fullName`,
                    exportType: AgGridExportType.TEXT,
                    field: `${selector}registerer.fullName`,
                    sortable: false,
                    filter: false,
                    flex: 1,
                    minWidth: 200,
                    hide: true
                },
                {
                    type: AgGridColumnType.TEXT,
                    headerName: i18n.tc(`scientificAnalysis.grid.results.case.administrative`),
                    exportField: `${selector}administrative.fullName`,
                    exportType: AgGridExportType.TEXT,
                    field: `${selector}administrative.fullName`,
                    sortable: false,
                    filter: false,
                    flex: 1,
                    minWidth: 200,
                    hide: true
                },
                {
                    type: AgGridColumnType.TEXT,
                    headerName: i18n.tc(`scientificAnalysis.grid.results.case.resident`),
                    exportField: `${selector}resident.fullName`,
                    exportType: AgGridExportType.TEXT,
                    field: `${selector}resident.fullName`,
                    sortable: false,
                    filter: false,
                    flex: 1,
                    minWidth: 200,
                    hide: true
                },
                {
                    type: AgGridColumnType.TEXT,
                    headerName: i18n.tc(`scientificAnalysis.grid.results.case.amendmentResponsible`),
                    exportField: `${selector}amendmentResponsible.fullName`,
                    exportType: AgGridExportType.TEXT,
                    field: `${selector}amendmentResponsible.fullName`,
                    sortable: false,
                    filter: false,
                    flex: 1,
                    minWidth: 200,
                    hide: true
                },
                {
                    type: AgGridColumnType.TEXT,
                    headerName: i18n.tc(`scientificAnalysis.grid.results.case.comprehensiveDiagnosis`),
                    exportField: `${selector}comprehensiveDiagnosis`,
                    exportType: AgGridExportType.TEXT,
                    field: `${selector}comprehensiveDiagnosis`,
                    sortable: false,
                    filter: false,
                    minWidth: 200,
                    width: 500,
                    hide: true,
                    cellStyle: { 'white-space': 'normal', 'overflow-y': 'auto' },
                    cellRendererFramework: CellGridRichText

                },
                {
                    type: AgGridColumnType.TEXT,
                    headerName: i18n.tc(`scientificAnalysis.grid.results.case.comprehensiveDiagnosisCodification`),
                    field: `${selector}comprehensiveDiagnosisCodification`,
                    sortable: false,
                    filter: false,
                    exportDisabled: true,
                    flex: 1,
                    minWidth: 200,
                    hide: true,
                    cellRenderer: ({ data }: ICellRendererParams) => {
                        let value = ``
                        if (data?.comprehensiveDiagnosisCodification?.length > 0) {
                            let codification
                            for (codification of data.comprehensiveDiagnosisCodification) {
                                if (codification.topographicCode) {
                                    if (value.length > 0) {
                                        value += ` ,`
                                    }
                                    value += codification.topographicCode.code + `|` + codification.topographicCode.description
                                }
                                if (codification.morphologicCode) {
                                    if (value.length > 0) {
                                        value += ` ,`
                                    }
                                    value += codification.morphologicCode.code + `|` + codification.morphologicCode.description
                                }
                            }
                        }
                        return value
                    }
                },
                {
                    type: AgGridColumnType.TEXT,
                    headerName: i18n.tc(`scientificAnalysis.grid.results.case.comprehensiveDiagnosisObservations`),
                    exportField: `${selector}comprehensiveDiagnosisObservations`,
                    exportType: AgGridExportType.TEXT,
                    field: `${selector}comprehensiveDiagnosisObservations`,
                    sortable: false,
                    filter: false,
                    flex: 1,
                    minWidth: 200,
                    hide: true
                },
                {
                    type: AgGridColumnType.TEXT,
                    headerName: i18n.tc(`scientificAnalysis.grid.results.case.totalURV`),
                    exportField: `${selector}totalURV`,
                    exportType: AgGridExportType.DOUBLE,
                    field: `${selector}totalURV`,
                    sortable: false,
                    filter: false,
                    flex: 1,
                    minWidth: 200,
                    hide: true
                },
                {
                    type: AgGridColumnType.TEXT,
                    headerName: i18n.tc(`scientificAnalysis.grid.results.case.totalUCL`),
                    exportField: `${selector}totalUCL`,
                    exportType: AgGridExportType.DOUBLE,
                    field: `${selector}totalUCL`,
                    sortable: false,
                    filter: false,
                    flex: 1,
                    minWidth: 200,
                    hide: true
                },
                {
                    type: AgGridColumnType.CHECK_ICON,
                    headerName: i18n.tc(`scientificAnalysis.grid.results.case.hasComplementary`),
                    exportField: `${selector}hasComplementary`,
                    exportType: AgGridExportType.BOOLEAN,
                    field: `${selector}hasComplementary`,
                    sortable: false,
                    filter: false,
                    flex: 1,
                    minWidth: 200,
                    hide: true
                },
                {
                    type: AgGridColumnType.CHECK_ICON,
                    headerName: i18n.tc(`scientificAnalysis.grid.results.case.amended`),
                    exportField: `${selector}amended`,
                    exportType: AgGridExportType.BOOLEAN,
                    field: `${selector}amended`,
                    sortable: false,
                    filter: false,
                    flex: 1,
                    minWidth: 200,
                    hide: true
                },
                {
                    headerName: i18n.tc(`scientificAnalysis.grid.results.case.consultationCase`),
                    headerTooltip: i18n.tc(`scientificAnalysis.grid.results.case.consultationCase`),
                    exportField: `${selector}consultationCase`,
                    exportType: AgGridExportType.BOOLEAN,
                    field: `${selector}consultationCase`,
                    sortable: false,
                    filter: false,
                    hide: true,
                    headerComponentParams: {
                        template: buildIconHeaderTemplate(`far fa-glasses`)
                    },
                    cellRenderer: ({ data }: ICellRendererParams) => data.value ? `<i class="success far fa-check"/>` : ``
                },
                {
                    type: AgGridColumnType.CHECK_ICON,
                    headerName: i18n.tc(`scientificAnalysis.grid.results.case.hasIncidents`),
                    headerTooltip: i18n.tc(`scientificAnalysis.grid.results.case.hasIncidents`),
                    exportField: `${selector}hasIncidents`,
                    exportType: AgGridExportType.BOOLEAN,
                    field: `${selector}hasIncidents`,
                    sortable: false,
                    filter: false,
                    headerComponentParams: {
                        template: buildIconHeaderTemplate(`far fa-exclamation-triangle`)
                    },
                    hide: true
                },
                {
                    type: AgGridColumnType.CHECK_ICON,
                    headerName: i18n.tc(`scientificAnalysis.grid.results.case.hasAttachments`),
                    headerTooltip: i18n.tc(`scientificAnalysis.grid.results.case.hasAttachments`),
                    exportField: `${selector}hasAttachments`,
                    exportType: AgGridExportType.BOOLEAN,
                    field: `${selector}hasAttachments`,
                    sortable: false,
                    filter: false,
                    headerComponentParams: {
                        template: buildIconHeaderTemplate(`far fa-paperclip`)
                    },
                    hide: true
                },
                {
                    type: AgGridColumnType.CHECK_ICON,
                    headerName: i18n.tc(`scientificAnalysis.grid.results.case.diagnosticNotifications`),
                    exportField: `${selector}diagnosticNotifications`,
                    exportType: AgGridExportType.BOOLEAN,
                    field: `${selector}diagnosticNotifications`,
                    sortable: false,
                    filter: false,
                    flex: 1,
                    minWidth: 200,
                    hide: true
                },
                {
                    type: AgGridColumnType.CHECK_ICON,
                    headerName: i18n.tc(`scientificAnalysis.grid.results.case.hasInternalConsultations`),
                    exportField: `${selector}hasInternalConsultans`,
                    exportType: AgGridExportType.BOOLEAN,
                    field: `${selector}hasInternalConsultans`,
                    sortable: false,
                    filter: false,
                    flex: 1,
                    minWidth: 200,
                    hide: true
                }
            ]
        }
    }

    public getGridColumns(): (ColDef | ExportedColDef | ExportedColGroupDef)[] {
        return [
            { type: AgGridColumnType.CUSTOM_CHECKBOX },
            ScientificAnalysisCaseDatasource.getEntityGridColumns(),
            ScientificAnalysisPatientDatasource.getEntityGridColumns(`patient.`, true)
        ]
    }

    public async paginator(_filterModel: CommonFilterModel, params: CommonParams, scrollId: string, totalElements: number): Promise<InfinitePaginatorResponse<ScientificAnalysisEntityCase>> {
        const response: InfinitePaginatorResponse<ScientificAnalysisEntityCase> = await scientificAnalysisService.search(this.scientificAnalysis, params, scrollId, totalElements) as InfinitePaginatorResponse<ScientificAnalysisEntityCase>
        this.setTotalElements(response.scrollPage.totalElements)
        return {
            content: response.content,
            scrollPage: {
                scrollId: encodeURIComponent(JSON.stringify(response?.scrollPage?.scrollId)),
                size: response.scrollPage.size,
                totalElements: response.scrollPage.totalElements
            }
        }
    }

    public getPatAndLabId(selectedRow: ScientificAnalysisEntityCase): [number, number] {
        return [selectedRow.patient.id, selectedRow.laboratoryId]
    }

    public getLabAndCaseId(selectedRow: ScientificAnalysisEntityCase): [number, Nullable<number>] {
        return [selectedRow.laboratoryId, selectedRow.id]
    }

    public getHTMLFieldsList(data: ScientificAnalysisEntityCase): string[] {
        const fields = [data.comprehensiveDiagnosis]
        return fields.filter(fieldValue => fieldValue)
    }
}
